import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import classNames from 'classnames';
import { IVideo } from '../../pages/admin/videos/hooks/useFetchVideos';
import { uploadVideoToIconik } from '../../pages/admin/videos/components/uploadVideoToIconik';
import { uploadThumbnailToIconik } from '../../pages/admin/videos/components/uploadThumbnailToIconik';
import { useUploadRegistry } from '../../hooks/useUploadRegistry';
import { useUser } from '../../hooks/useUser';
import { hasVideoFile } from './videoStatus';
import { StatusPill } from './StatusPill';

export const UPLOAD_ROLES = ['ADMIN', 'POST_PRODUCTION_ADMIN', 'ACCOUNT_MANAGER_ADMIN'];
export const canUploadRole = (role?: string | null) => !!role && UPLOAD_ROLES.includes(role);

const upIc = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20V9m0 0-4 4m4-4 4 4" />
    <path d="M5 5h14" />
  </svg>
);

type Phase = 'idle' | 'uploading' | 'processing';

/**
 * §10 — upload with visible states. Ports VideoCardV2's iconik upload flow;
 * the dropzone mounts ONLY for upload-capable roles (old §9.5: it used to
 * mount on every card for every role).
 */
export const UploadableVideoCard: FC<{
  video: IVideo;
  project: any;
  refetch: () => void;
  children: (uploadSlot: ReactNode) => ReactNode;
}> = ({ video, project, refetch, children }) => {
  const { role } = useUser();
  const uploadRegistry = useUploadRegistry();
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<number | null>(null);
  const [fileName, setFileName] = useState('');

  const hasFile = hasVideoFile(video);
  const canUpload = canUploadRole(role);
  const canUploadVideo = canUpload && (!hasFile || video.final === false);
  const canUploadThumbnail = canUpload && hasFile;
  const slotKey = video.id ?? video.hash;

  const handleDrop = async (accepted: File[]) => {
    const file = accepted[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    if (isImage && !canUploadThumbnail) {
      toast.error('Upload a video to this slot before adding thumbnails');
      return;
    }
    if (!isImage && !canUploadVideo) return;
    setFileName(file.name);
    setPhase('uploading');
    setProgress(0);
    uploadRegistry.add(slotKey);
    try {
      if (isImage) {
        if (!video.id || !video.hash || !video.version) throw new Error('Video metadata missing; cannot upload thumbnail');
        await uploadThumbnailToIconik(file, video, (pct: number) => setProgress(pct));
        toast.success('Thumbnail uploaded');
        uploadRegistry.remove(slotKey);
        setPhase('idle');
        setProgress(null);
      } else {
        if (!video.hash) throw new Error('This video is missing its slot id — reopen the project to refresh, then try again.');
        await uploadVideoToIconik(file, video, (pct: number) => setProgress(pct));
        uploadRegistry.remove(slotKey);
        setProgress(100);
        setPhase('processing');
      }
      refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed');
      uploadRegistry.remove(slotKey);
      setPhase('idle');
      setProgress(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    accept: {
      'video/mp4': ['.mp4'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/tiff': ['.tif', '.tiff'],
    },
    multiple: false,
    disabled: !(canUploadVideo || canUploadThumbnail) || phase !== 'idle',
    noClick: true,
    noKeyboard: true,
  });

  // Leave "processing" once the server reflects the ingested file.
  useEffect(() => {
    setPhase((prev) => (prev === 'processing' ? 'idle' : prev));
    setProgress((prev) => (prev === 100 ? null : prev));
    // eslint-disable-next-line
  }, [video.key, video.version, video.final]);

  // §10.4 — the 60s watchdog keeps its visible ceiling.
  useEffect(() => {
    if (phase !== 'processing') return undefined;
    const timer = setTimeout(() => {
      setPhase('idle');
      setProgress(null);
    }, 60000);
    return () => clearTimeout(timer);
  }, [phase]);

  const uploadSlot: ReactNode = canUploadVideo ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        open();
      }}
      className="inline-flex items-center gap-1.5 h-[30px] px-[11px] rounded-lg border border-dashed border-accent bg-tint text-accent-text font-bold whitespace-nowrap shrink-0"
      style={{ fontSize: 11.5 }}
    >
      {upIc}
      Upload
    </button>
  ) : null;

  if (!canUpload) return <>{children(null)}</>;

  return (
    <div {...getRootProps()} className="relative min-w-0">
      <input {...getInputProps()} />
      {children(uploadSlot)}
      {isDragActive && (
        <div className="absolute inset-0 z-10 rounded-xl border-2 border-dashed border-accent bg-surface flex items-center justify-center">
          <div className="absolute inset-0 rounded-xl bg-tint" />
          <div className="relative flex flex-col items-center gap-1.5 text-accent-text px-4 text-center">
            {upIc}
            <div className="font-bold" style={{ fontSize: 13.5 }}>
              Drop .mp4 to upload
            </div>
            {canUploadThumbnail && (
              <div style={{ fontSize: 11.5, opacity: 0.8 }}>or an image to set the thumbnail</div>
            )}
          </div>
        </div>
      )}
      {phase === 'uploading' && (
        <div className="absolute inset-0 z-10 rounded-xl bg-surface border border-border-token p-[18px] flex flex-col justify-center gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex-1 min-w-0 font-bold text-text-1 truncate" style={{ fontSize: 12.5 }}>
              {fileName}
            </span>
            <span className="font-bold text-accent-text tabular-nums" style={{ fontSize: 12.5 }}>
              {progress ?? 0}%
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-track overflow-hidden">
            <i className="block h-full rounded-full bg-accent" style={{ width: `${progress ?? 0}%` }} />
          </div>
        </div>
      )}
      {phase === 'processing' && (
        <div className="absolute inset-0 z-10 rounded-xl bg-surface border border-border-token p-4 flex flex-col items-center justify-center gap-2 text-center">
          <span
            className="w-[30px] h-[30px] rounded-full inline-block animate-ppSpin"
            style={{ border: '2px solid var(--track)', borderTopColor: 'var(--accent)' }}
          />
          <div className="font-bold text-text-1" style={{ fontSize: 13 }}>
            Processing
          </div>
          <div className="text-text-2" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
            Transcoding can take up to a minute. You can leave this page.
          </div>
          <StatusPill status="production" label="In Production" />
        </div>
      )}
    </div>
  );
};

/** §10.1 — the channel tile: uploads into the group's first open slot. */
export const ChannelUploadTile: FC<{ videos: IVideo[]; project: any; refetch: () => void }> = ({
  videos,
  project,
  refetch,
}) => {
  const { role } = useUser();
  const uploadRegistry = useUploadRegistry();
  const target = useMemo(
    // An empty slot wins over an unapproved deliverable, so the tile never
    // silently replaces a video under review while an open slot exists. (With
    // no empty slot it still targets the first unapproved — per-card behavior.)
    () => videos.find((v) => !hasVideoFile(v)) ?? videos.find((v) => v.final === false),
    [videos]
  );
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);

  const onDrop = async (accepted: File[]) => {
    const file = accepted[0];
    if (!file || !target) return;
    if (!target.hash) {
      toast.error('This channel has no open slot to upload into.');
      return;
    }
    // Register the slot for the same reason UploadableVideoCard does: the
    // granular refresh treats unregistered slots as idle and can clobber an
    // upload in flight.
    const slotKey = target.id ?? target.hash;
    uploadRegistry.add(slotKey);
    setBusy(true);
    setProgress(0);
    try {
      await uploadVideoToIconik(file, target, (pct: number) => setProgress(pct));
      toast.success('Upload complete. Ingesting...');
      refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      uploadRegistry.remove(slotKey);
      setBusy(false);
      setProgress(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'video/mp4': ['.mp4'] },
    multiple: false,
    disabled: busy,
    noClick: true,
    noKeyboard: true,
  });

  if (!canUploadRole(role) || !target) return null;

  return (
    <div
      {...getRootProps()}
      onClick={open}
      className={classNames(
        'flex items-center justify-center flex-wrap gap-2.5 px-4 py-[18px] border border-dashed rounded-xl cursor-pointer transition-colors',
        isDragActive ? 'border-accent bg-tint' : 'border-muted bg-page hover:border-accent'
      )}
    >
      <input {...getInputProps()} />
      <span className="text-accent-text">{upIc}</span>
      <span className="font-bold text-text-1" style={{ fontSize: 13 }}>
        {busy ? `Uploading… ${progress ?? 0}%` : 'Upload a deliverable'}
      </span>
      <span className="text-text-2" style={{ fontSize: 12.5 }}>
        or drop an .mp4 anywhere in this channel
      </span>
    </div>
  );
};
